import axios, { AxiosError, AxiosResponse } from 'axios';
import { Activity, ActivityFormValues } from '../models/activity';
import { toast } from 'react-toastify';
import { router } from '../router/Routes';
import { store } from '../stores/store';
import { User, UserFormValues } from '../models/user';
import { Photo, Profile, UserActivity } from '../models/profile';
import { PaginatedResult } from '../models/pagination';

const sleep = (delay: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
};

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

//  interceptor for request to add Auth token to header
axios.interceptors.request.use((config) => {
  const token = store.commonStore.token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

//  interceptor for response to delay the response by 1 second
axios.interceptors.response.use(
  async (response) => {
    if (process.env.NODE_ENV === 'development') await sleep(1000);
    //  check if response has pagination header
    const pagination = response.headers['pagination'];
    if (pagination) {
      //  convert response.data to PaginatedResult with data and pagination returned from server
      response.data = new PaginatedResult(
        response.data,
        JSON.parse(pagination),
      );

      return response as AxiosResponse<PaginatedResult<any>>;
    }
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      const { status, data, config, headers } = error.response as AxiosResponse;

      switch (status) {
        case 400:
          if (config.method === 'get' && data.errors['id']) {
            router.navigate('/notfound');
          }
          if (data.errors) {
            const modalStateErrors = [];
            for (const key in data.errors) {
              if (data.errors[key] && data.errors[key].length > 0) {
                modalStateErrors.push(...data.errors[key]);
              }
            }
            throw modalStateErrors;
          } else {
            toast.error(data);
          }
          break;
        case 401:
          if (
            status === 401 &&
            headers['www-authenticate']?.startsWith(
              'Bearer error="invalid_token"',
            )
          ) {
            store.userStore.logout();
            toast.error('Session expired - please login again');
          }
          break;
        case 403:
          toast.error('Forbidden');
          break;
        case 404:
          router.navigate('/notfound');
          break;
        case 500:
          store.commonStore.setServerError(data);
          router.navigate('/server-error ');
          break;
      }
    }

    return Promise.reject(error);
  },
);

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const requests = {
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  post: <T>(url: string, body: object) =>
    axios.post<T>(url, body).then(responseBody),
  put: <T>(url: string, body: object) =>
    axios.put<T>(url, body).then(responseBody),
  del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
};

const Activities = {
  list: (params: URLSearchParams) =>
    axios
      .get<PaginatedResult<Activity[]>>('/activities', { params })
      .then(responseBody),
  details: (id: string) => requests.get<Activity>(`/activities/${id}`),
  create: (activity: ActivityFormValues) =>
    requests.post<void>('/activities', activity),
  update: (activity: ActivityFormValues) =>
    requests.put<void>(`/activities/${activity.id}`, activity),
  delete: (id: string) => requests.del<void>(`/activities/${id}`),
  attend: (id: string) => requests.post<void>(`/activities/${id}/attend`, {}),
};

const Account = {
  current: () => requests.get<User>('/account'),
  login: (user: UserFormValues) => requests.post<User>('/account/login', user),
  register: (user: UserFormValues) =>
    requests.post<User>('/account/register', user),
  fbLogin: (accessToken: string) =>
    requests.post<User>(`/account/fblogin?accessToekn=${accessToken}`, {}),
  refreshToken: () => requests.post<User>('/account/refreshToken', {}),
  verifyEmail: (token: string, email: string) =>
    requests.post<void>(
      `/account/verifyEmail?token=${token}&email=${email}`,
      {},
    ),
  resendEmailConfirm: (email: string) =>
    requests.get(`/account/resendEmailConfirmationLink?email=${email}`),
};

const Profiles = {
  get: (username: string) => requests.get<Profile>(`/profiles/${username}`),
  edit: (username: string, profile: Partial<Profile>) =>
    requests.put(`/profiles/${username}/edit`, profile),
  uploadPhoto: (file: Blob) => {
    const formData = new FormData();
    formData.append('File', file);
    return axios.post<Photo>('photos', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  setMainPhoto: (id: string) => requests.post(`/photos/${id}/setMain`, {}),
  deletePhoto: (id: string) => requests.del(`/photos/${id}`),
  updateFollowing: (username: string) =>
    requests.post(`/follow/${username}`, {}),
  listFollowing: (username: string, predicate: string) =>
    requests.get<Profile[]>(`/follow/${username}?predicate=${predicate}`),
  listUserActivities: (username: string, predicate: string) =>
    requests.get<UserActivity[]>(
      `/profiles/${username}/activities?predicate=${predicate}`,
    ),
};

const agent = {
  Activities,
  Account,
  Profiles,
};

export { agent };
